import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, useAuth } from '@halo/web-lib';
import { LoadingScreen } from './LoadingScreen';

interface StoreOnboarding {
  id: number;
  onboardingStep: number;
  onboardedAt: string | null;
}

function useOnboardingStatus() {
  const { storeId } = useAuth();
  return useQuery({
    queryKey: ['onboarding-status', storeId],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreOnboarding[]>('/api/v1/auth/stores', { signal });
      if (!res.success || !res.data) return null;
      return res.data.find((s) => s.id === Number(storeId)) ?? null;
    },
    enabled: !!storeId,
    staleTime: 30_000,
  });
}

function isOnboarded(status: StoreOnboarding | null | undefined): boolean {
  if (!status) return false;
  return Boolean(status.onboardedAt) || status.onboardingStep >= 4;
}

/**
 * Route guard that redirects to /onboarding if the current store
 * hasn't completed onboarding (step < 4 and no onboardedAt).
 */
export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { data: onboardingStatus, isLoading } = useOnboardingStatus();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (onboardingStatus && !isOnboarded(onboardingStatus)) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * Inverse guard for the /onboarding route. Once a store has completed
 * onboarding it shouldn't be able to revisit the wizard — redirect to
 * /integrations instead.
 */
export function OnboardingCompletedRedirect({ children }: { children: React.ReactNode }) {
  const { data: onboardingStatus, isLoading } = useOnboardingStatus();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isOnboarded(onboardingStatus)) {
    return <Navigate to="/integrations" replace />;
  }

  return <>{children}</>;
}
